import type { MaterialPair } from './variantLabel';

// EVERY SUBMODEL, THE SAME NUMBER OF MATERIALS. Arithmetic, not a model.
//
// The listings are read one page per call, in series (runListing has to be serial or the
// merchant throttles it and truncates the gallery), so no call can see what its siblings
// answered and each describes the product at whatever level of detail its own page uses.
// Three listings of this chair name a fabric and a wood; the fourth also spells out the
// polyester and the foam inside the seat. The result is a table whose rows do not line up:
// submodel 3's wood on a different line from submodel 1's, with nothing saying they are the
// same question.
//
// This was a Gemini call at first, asked to reconcile them. It came back with no slots at
// all — and that is the argument against it rather than a bug to fix: the whole job is
// counting, the rule is one sentence long, and a call that can fail silently on a question
// arithmetic answers exactly is a call that should not be made. It costs money, it takes a
// round trip at the end of the longest wait in the app, and it cannot be tested.
//
// THE RULE: the number of materials is the number MOST of the listings name. A listing that
// names more is describing the same product in more detail, not a different product.

// A material name reduced to what it means, for asking whether two listings named the same
// substance. The listings SHOUT some of them and title-case others.
const fold = (s: string) => s.trim().toLowerCase();

// The count most submodels agree on.
//
// TIES GO TO THE SMALLER. Two listings naming two materials and two naming three is a
// genuine disagreement, and the shape that admits fewer is the one more of the listings can
// fill without a hole: an empty cell invented for every submodel is a worse answer than a
// value set aside on one.
function commonCount(counts: number[]): number {
  const votes = new Map<number, number>();
  for (const n of counts) votes.set(n, (votes.get(n) ?? 0) + 1);
  let best = 0;
  let bestVotes = -1;
  for (const [n, v] of [...votes.entries()].sort((a, b) => a[0] - b[0])) {
    if (v > bestVotes) {
      best = n;
      bestVotes = v;
    }
  }
  return best;
}

// One submodel's materials: exactly `commonCount` of them, in the listing's own order. Null
// where that listing named fewer than the majority — an empty cell is an answer ("this page
// did not describe that part") and is never filled in from a sibling, which is a different
// colourway.
//
// WHAT DOES NOT FIT IS DROPPED, and dropped here rather than carried along unused. It was
// kept for a while as an `extra` and shown under the submodel's name, on the grounds that a
// value nobody can see is a value somebody goes looking for. But this table is the product's
// materials at the level all its listings describe them, and a fourth listing's foam is a
// detail about one page rather than a fact this shape is missing. Carrying it meant every
// reader of this function had a field to decide what to do about, and every answer was "not
// show it".
export type AlignedSubmodel = (MaterialPair | null)[];

// Which of one listing's pairs are the ones the others also name.
//
// WHICH TO KEEP IS THE ONLY REAL DECISION HERE, and position is the wrong answer to it: a
// listing that happens to put its foam first would keep the foam and set the wood aside. So
// each pair is scored by HOW MANY OTHER SUBMODELS NAME THE SAME MATERIAL — that is what
// makes a material one of the product's own rather than one page's extra detail — and the
// original order breaks ties, since a listing lists its main materials first.
//
// On this chair: "bois d'hévéa massif" is named by all four, "tissu" by three, "polyester"
// and "mousse" by one. Keeping two keeps the fabric and the wood, in that order.
function keepBest(
  pairs: MaterialPair[], keep: number, elsewhere: Map<string, number>, own: string[],
): MaterialPair[] {
  if (pairs.length <= keep) return pairs;
  const scored = pairs.map((pair, at) => {
    const key = fold(pair.material);
    // Minus its own vote: the question is how many OTHER listings name it.
    const shared = (elsewhere.get(key) ?? 0) - (own.includes(key) ? 1 : 0);
    return { pair, at, shared };
  });
  const chosen = new Set(
    [...scored]
      .sort((a, b) => (b.shared - a.shared) || (a.at - b.at))
      .slice(0, keep)
      .map((x) => x.at),
  );
  return scored.filter((x) => chosen.has(x.at)).map((x) => x.pair);
}

// Line every submodel's materials up on one count. Order in, order out: the result is
// parallel to the submodels given.
export function alignMaterials(
  submodels: readonly { materials: MaterialPair[] }[],
): AlignedSubmodel[] {
  const lists = submodels.map((s) => (s.materials || []).filter((p) => p.material || p.color));
  // Empty lists do not vote. A link with nothing read yet would otherwise drag the count to
  // zero and empty the table for every submodel that HAS been read.
  const counted = lists.filter((l) => l.length).map((l) => l.length);
  if (!counted.length) return lists.map(() => []);
  const keep = commonCount(counted);

  // How many submodels name each material, once each: a listing that says "tissu" twice has
  // not made it more common.
  const elsewhere = new Map<string, number>();
  const keysOf = lists.map((l) => [...new Set(l.map((p) => fold(p.material)))]);
  for (const keys of keysOf) {
    for (const key of keys) elsewhere.set(key, (elsewhere.get(key) ?? 0) + 1);
  }

  return lists.map((pairs, i) => {
    // Padded at the END. A short listing said less; it did not say something about a
    // different part, and there is nothing here that could know which of the majority's
    // materials it left out.
    const padded: AlignedSubmodel = [...keepBest(pairs, keep, elsewhere, keysOf[i])];
    while (padded.length < keep) padded.push(null);
    return padded;
  });
}
